import React, { useState, useEffect } from 'react';
import { Gift, Heart, ArrowRight, Clock, CheckCircle } from 'lucide-react';
import { injectCSSVariables } from '../colors/theme';

const HomePage = ({ setCurrentPage }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [animateHero, setAnimateHero] = useState(false);

  // Inject CSS variables for the current theme on mount
  useEffect(() => {
    injectCSSVariables();
    setAnimateHero(true);
  }, []);

  // Auto-rotate testimonials
  const testimonials = [
    { text: "Fant den perfekte gaven på 2 minutter!", author: "Maria, Oslo" },
    { text: "Endelig en gaveguide som faktisk fungerer", author: "Lars, Bergen" },
    { text: "Kona ble så glad for gaven jeg fant her!", author: "Erik, Trondheim" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Quick categories for direct navigation
  const quickCategories = [
    { id: 'wedding', name: 'Bryllup', emoji: '💍', popular: true },
    { id: 'birthday', name: 'Bursdag', emoji: '🎂', popular: true },
    { id: 'christmas', name: 'Jul', emoji: '🎄', popular: true },
    { id: 'for-him', name: 'Til han', emoji: '👨', popular: false },
    { id: 'for-her', name: 'Til henne', emoji: '👩', popular: false },
    { id: 'for-kids', name: 'Barn', emoji: '👶', popular: false }
  ];

  return (
    <div className="min-h-screen bg-[var(--color-secondary-50)]">

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/landing.png"
            alt="Magisk gaveboks med gylne partikler som symboliserer den perfekte gaven"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-[var(--color-warning-300)] rounded-full opacity-70 animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Hero Content */}
        <div
          className={`relative z-10 container mx-auto px-4 text-center transition-all duration-1000 ${
            animateHero ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <div className="max-w-lg mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-[var(--color-secondary-50)] mb-6 leading-tight">
              Finn den
              <span className="block text-[var(--color-warning-300)] font-extrabold relative">
                perfekte
              </span>
              gaven på 60 sekunder
            </h1>

            <p className="text-xl md:text-2xl text-[var(--color-secondary-200)] mb-8 font-medium">
              Slipp å gå tom for gaveideer. Vår AI finner gaven som garantert treffer blink!
            </p>

            <div className="space-y-4">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="w-full sm:w-auto px-8 py-5 md:px-12 md:py-6 rounded-2xl font-bold text-xl md:text-2xl group relative overflow-hidden transition-all transform hover:scale-105"
                style={{
                  backgroundImage: 'var(--gradient-primary)',
                  boxShadow: 'var(--shadow-primary)'
                }}
              >
                <div className="absolute inset-0 bg-[var(--color-accent-500)] opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                <div className="relative flex items-center justify-center space-x-3">
                  <Gift className="w-7 h-7 md:w-8 md:h-8 text-[var(--color-secondary-50)]" />
                  <span className="text-[var(--color-secondary-50)]">Start gavefinneren GRATIS</span>
                  <ArrowRight className="w-6 h-6 text-[var(--color-secondary-50)] group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section
        className="py-8 border-y-2 border-[var(--color-secondary-200)]"
        style={{ backgroundImage: 'var(--gradient-accent)' }}
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center space-y-2">
              <div className="w-12 h-12 bg-[var(--color-success-100)] rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[var(--color-success-600)]" />
              </div>
              <h3 className="font-semibold text-[var(--color-secondary-900)]">100% Gratis</h3>
              <p className="text-sm text-[var(--color-secondary-600)]">Ingen skjulte kostnader</p>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <div className="w-12 h-12 bg-[var(--color-accent-100)] rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-[var(--color-accent-600)]" />
              </div>
              <h3 className="font-semibold text-[var(--color-secondary-900)]">Under 1 minutt</h3>
              <p className="text-sm text-[var(--color-secondary-600)]">Få forslag med én gang</p>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <div className="w-12 h-12 bg-[var(--color-warning-100)] rounded-full flex items-center justify-center">
                <Heart className="w-6 h-6 text-[var(--color-warning-600)]" />
              </div>
              <h3 className="font-semibold text-[var(--color-secondary-900)]">Personlige forslag</h3>
              <p className="text-sm text-[var(--color-secondary-600)]">Tilpasset din situasjon</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Carousel */}
      <section className="py-8 bg-[var(--color-secondary-50)]">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="transition-all duration-500 transform">
              <p className="text-lg md:text-xl text-[var(--color-secondary-700)] italic mb-4">
                "{testimonials[currentTestimonial].text}"
              </p>
              <p className="text-sm text-[var(--color-secondary-500)] font-medium">
                - {testimonials[currentTestimonial].author}
              </p>
            </div>
            <div className="flex justify-center space-x-2 mt-4">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentTestimonial ? 'bg-[var(--color-primary-500)] w-6' : 'bg-[var(--color-secondary-300)]'
                  }`}
                  onClick={() => setCurrentTestimonial(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Categories */}
      <section className="py-12 bg-[var(--color-secondary-50)]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-[var(--color-secondary-900)] mb-8">
              Eller velg direkte kategori:
            </h2>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
              {quickCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setCurrentPage(category.id)}
                  className="relative bg-[var(--color-secondary-50)] border-2 rounded-xl p-4 md:p-6 hover:shadow-lg transition-all transform hover:scale-105 group"
                  style={{ borderColor: 'var(--color-secondary-200)' }}
                >
                  {category.popular && (
                    <div className="absolute -top-2 -right-2 text-xs px-2 py-1 rounded-full font-bold text-[var(--color-secondary-50)]" style={{ backgroundColor: 'var(--color-error-500)' }}>
                      Populær
                    </div>
                  )}
                  <div className="text-2xl md:text-3xl mb-2 group-hover:scale-110 transition-transform">
                    {category.emoji}
                  </div>
                  <h3 className="text-sm md:text-base font-semibold group-hover:text-[var(--color-primary-600)]">
                    {category.name}
                  </h3>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section
        className="py-16 relative overflow-hidden"
        style={{ backgroundImage: 'var(--gradient-hero)' }}
      >
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute opacity-20 animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`
              }}
            >
              🎁
            </div>
          ))}
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-secondary-50)] mb-6">
              Slipp å stresse med gavekjøp!
            </h2>
            <p className="text-xl text-[var(--color-primary-100)] mb-8">
              La vår AI gjøre jobben - få personlige gaveforslag på under 60 sekunder
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="w-full sm:w-auto px-8 py-5 md:px-12 md:py-6 rounded-2xl font-bold text-xl md:text-2xl group transition-all transform hover:scale-105"
              style={{
                backgroundColor: 'var(--color-secondary-50)',
                color: 'var(--color-primary-600)',
                boxShadow: 'var(--shadow-accent)'
              }}
            >
              <div className="flex items-center justify-center space-x-3">
                <span>Finn min perfekte gave nå!</span>
                <ArrowRight className="w-6 h-6" />
              </div>
            </button>
            <p className="text-sm mt-4" style={{ color: 'var(--color-primary-100)' }}>
              ✓ Helt gratis ✓ Ingen registrering ✓ Umiddelbare resultater
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

// Floating animation CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(180deg); }
  }
  .animate-float {
    animation: float 6s ease-in-out infinite;
  }
`;
document.head.appendChild(style);

export default HomePage;
